import { Link } from "react-router-dom";
import { categories, platformStats, teamMembers } from "@/mocks/streams";
import { usePageMeta } from "@/hooks/usePageMeta";

const milestones = [
  { year: "2019", title: "有因直播正式上线", desc: "以游戏直播为起点，首批签约主播 200 位，搭建起最基础的互动直播能力。" },
  { year: "2021", title: "拓展多元内容生态", desc: "陆续开设娱乐、生活、音乐、户外等分类，日活跃观众突破百万。" },
  { year: "2023", title: "推出创作者成长计划", desc: "面向新主播提供流量扶持与培训课程，帮助超过两万名创作者获得第一笔直播收入。" },
  { year: "2025", title: "全面升级互动体验", desc: "上线高清直播与实时互动能力，形成覆盖 48 个分类的综合直播平台。" },
  { year: "2026", title: "连接更多元的直播场景", desc: "探索科技、体育、公益等新场景，让更多真实的人与事被看见。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 有因直播",
    "了解有因直播的平台定位、发展历程、服务范围与团队介绍，有因直播致力于让每一次直播都成为真实的相遇。",
  );

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-10">
      <section className="overflow-hidden rounded-xl border border-background-200/70 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="p-6 md:p-10">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/12 px-3 py-1 text-xs text-primary-500">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-planet-line"></i>
              </span>
              关于有因直播
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              让每一次直播
              <br />
              都成为一次真实的相遇
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-500">
              有因直播是一个面向广大网络用户的综合直播平台，提供游戏、娱乐、生活等多个领域的实时直播内容。
              我们相信，直播不只是内容的展示，更是人与人之间真实的连接与陪伴。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">
              我们希望通过稳定流畅的直播体验、丰富多元的内容生态和真诚友好的社区氛围，让每一位主播都能被看见，
              让每一位观众都能找到共鸣。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                加入我们
              </Link>
              <Link
                to="/search"
                className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 whitespace-nowrap"
              >
                开始探索直播
              </Link>
            </div>
          </div>
          <div className="relative h-64 lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20abstract%20live%20streaming%20visual%20with%20glowing%20orange%20broadcast%20waves%20and%20connected%20light%20particles%20over%20a%20dark%20studio%20background%2C%20stylized%20tech%20art%2C%20cinematic%20high%20detail%2C%20vibrant%20contrast&width=960&height=720&seq=yy-about-hero&orientation=landscape"
              alt="有因直播平台介绍"
              title="有因直播平台"
              className="h-full w-full object-top object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent lg:bg-gradient-to-r" />
          </div>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {platformStats.map((stat) => (
          <div key={stat.id} className="rounded-lg border border-background-200/70 bg-background-100 p-5 text-center">
            <p className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{stat.value}</p>
            <p className="mt-2 text-sm text-foreground-400">{stat.label}</p>
          </div>
        ))}
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-background-200/70 bg-background-100 p-6">
          <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500/12 text-primary-500">
            <i className="ri-focus-3-line text-xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">我们的定位</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-500">
            面向大众的综合直播平台，用内容连接主播与观众，覆盖游戏、娱乐、生活等多元领域。
          </p>
        </div>
        <div className="rounded-xl border border-background-200/70 bg-background-100 p-6">
          <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/12 text-accent-400">
            <i className="ri-flag-2-line text-xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">我们的使命</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-500">
            让每一个真实的人都能被看见，让每一次互动都充满温度，营造健康、友好的直播社区氛围。
          </p>
        </div>
        <div className="rounded-xl border border-background-200/70 bg-background-100 p-6">
          <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-500/15 text-secondary-500">
            <i className="ri-shield-star-line text-xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">我们的坚持</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-500">
            内容真实、审核严格、体验流畅，持续投入直播技术与创作者扶持，拒绝低质内容。
          </p>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">发展历程</h2>
        <p className="mt-1 text-sm text-foreground-400">从一间小工作室，到今天的综合直播平台</p>
        <div className="mt-6 space-y-4">
          {milestones.map((item, index) => (
            <div key={item.year} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className="w-10 h-10 flex flex-shrink-0 items-center justify-center rounded-full bg-primary-500/15 font-heading text-xs font-semibold text-primary-500">
                  {item.year}
                </span>
                {index < milestones.length - 1 && <span className="mt-1 w-px flex-1 bg-background-200" />}
              </div>
              <div className="flex-1 rounded-lg border border-background-200/70 bg-background-100 p-5 pb-6">
                <h3 className="font-heading text-sm font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">服务范围</h2>
        <p className="mt-1 text-sm text-foreground-400">覆盖主流直播分类，持续拓展新的内容场景</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-100 px-4 py-4 transition-colors hover:border-primary-500/60"
            >
              <span className="w-9 h-9 flex flex-shrink-0 items-center justify-center rounded-md bg-primary-500/12 text-primary-500 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className={`${cat.icon} text-lg`}></i>
              </span>
              <span>
                <span className="block text-sm text-foreground-800">{cat.name}直播</span>
                <span className="block text-[11px] text-foreground-400">{cat.tagline}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">团队介绍</h2>
        <p className="mt-1 text-sm text-foreground-400">一群热爱直播、相信内容价值的人（以下为示例团队成员）</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {teamMembers.map((member) => (
            <div key={member.id} className="rounded-lg border border-background-200/70 bg-background-100 p-5 text-center">
              <div className="mx-auto w-20 h-20 overflow-hidden rounded-full border border-background-300">
                <img src={member.avatar} alt={member.name} className="h-full w-full object-cover" />
              </div>
              <h3 className="mt-4 font-heading text-sm font-semibold text-foreground-950">{member.name}</h3>
              <p className="mt-1 text-xs text-primary-500">{member.role}</p>
              <p className="mt-3 text-xs leading-relaxed text-foreground-400">{member.intro}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 flex flex-col gap-4 rounded-xl border border-background-200/70 bg-background-100 p-6 sm:flex-row sm:items-center sm:justify-between md:p-8">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">版权与隐私</h2>
          <p className="mt-1 text-sm text-foreground-400">
            本网站为前端演示版本，页面内容与主播信息均为示例数据。了解更多请查看版权说明与隐私政策。
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/contact"
            className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 whitespace-nowrap"
          >
            隐私政策
          </Link>
          <Link
            to="/contact"
            className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 whitespace-nowrap"
          >
            服务条款
          </Link>
        </div>
      </section>
    </div>
  );
}